import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "../App.css";
import "../css/AdminUsers.css";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Account level mapping
  const accountLevels = {
    0: "User",
    1: "Locked",
    3: "Playwright",
    4: "Administrator", // still display current admins
  };

  // Only allow changing to non-admin levels (0, 1, 3)
  const levelOptions = [0, 1, 3];

  // Fetch all users from DB
  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      const userList = Array.isArray(res.data) ? res.data : res.data.users || [];
      setUsers(userList);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Update user account level with confirmation
  const handleAccountChange = async (id, currentLevel, newLevel) => {
    if (currentLevel === newLevel) return;

    const confirmChange = window.confirm(
      `Are you sure you want to change this user's account level from "${accountLevels[currentLevel]}" to "${accountLevels[newLevel]}"?`
    );
    if (!confirmChange) return;

    try {
      await axios.put(
        `${API_URL}/api/admin/users/${id}/account`,
        { account: newLevel },
        { withCredentials: true }
      );
      fetchUsers();
    } catch (err) {
      console.error("Failed to update account level:", err);
      alert("Failed to update account level. Check console for details.");
    }
  };

  // Delete a user
  const handleDeleteUser = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await axios.delete(`${API_URL}/api/admin/users/${id}`, { withCredentials: true });
      fetchUsers();
    } catch (err) {
      console.error("Failed to delete user:", err);
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter((user) => {
    const search = searchTerm.toLowerCase();
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = user.email.toLowerCase();
    return fullName.includes(search) || email.includes(search);
  });

  return (
    <div className="admin-users-wrapper">
      <h1>Admin User Management</h1>

      {/* Search bar */}
      <input
        type="text"
        placeholder="Search by name or email..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="admin-search-bar"
        style={{ marginBottom: "1rem", padding: "0.5rem", width: "100%" }}
      />

      <table className="admin-users-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Account Level</th>
            <th>Change Level</th>
            <th>Delete</th>
          </tr>
        </thead>

        <tbody>
          {filteredUsers.map((user) => (
            <tr key={user._id}>
              <td>{user.firstName} {user.lastName}</td>
              <td>{user.email}</td>
              <td>{accountLevels[user.account] || "Unknown"}</td>

              <td>
                {user.account === 4
                  ? null // no buttons for admins
                  : levelOptions.map((level) => (
                      <button
                        key={level}
                        className="level-btn"
                        disabled={user.account === level}
                        onClick={() => handleAccountChange(user._id, user.account, level)}
                      >
                        {accountLevels[level]}
                      </button>
                    ))
                }
              </td>

              <td>
                <button
                  className="delete-user-btn"
                  onClick={() => handleDeleteUser(user._id)}
                >
                  ❌ Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
